import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ACCOUNT_TYPES, ACTIONS, APP_APPROVALS, APP_CATEGORIES, DECISION_LABELS, explainRule, SENSITIVITY_LEVELS, SEVERITIES, type Decision, type PolicyRule, type RuleConditions } from '@ward/core';
import { api, useAction, useApi } from '../api';
import { useCan } from '../auth';
import { Ago, catLabel, DecisionTag, Empty, EntityLink, fmtDate, MutationError, Notice, Outcome, PageHead, Q, Section, Seg, statusTone } from '../ui/kit';
import { DECISION } from '../ui/format';
import { MultiPick, SinglePick, summarize, Token } from '../ui/token';
import { ConfirmDialog } from '../ui/confirm';
import { actionLabel } from './Activity';

interface PolicyForm {
  name: string;
  description: string;
  enabled: boolean;
  priority: number;
  conditions: RuleConditions;
  action: Decision;
  options: PolicyRule['options'];
}

const BROWSER_SHORT: Record<string, string> = { edge: 'Edge', chrome: 'Chrome', firefox: 'Firefox', safari: 'Safari', other: 'other' };
const DEFAULT_OPTIONS: PolicyRule['options'] = { alert: false, severity: 'medium', notifyIntegrations: false, acknowledgeMinutes: 10 };

export const TEMPLATES: Record<string, { label: string; form: Partial<PolicyForm> }> = {
  'personal-ai': {
    label: 'Block sensitive data to personal AI',
    form: { name: 'Block sensitive data to personal AI', action: 'block', conditions: { appCategories: { op: 'in', values: ['genai'] }, accountTypes: ['personal', 'unknown'], actions: ['paste', 'upload', 'submit'], data: { minSensitivity: 'confidential' } }, options: { ...DEFAULT_OPTIONS, alert: true, severity: 'high', userMessage: 'Use your approved corporate AI account for work data.' } },
  },
  'unknown-ai': {
    label: 'Warn on unreviewed AI tools',
    form: { name: 'Warn on submissions to unreviewed AI tools', action: 'warn', conditions: { appCategories: { op: 'in', values: ['genai'] }, appApproval: ['unreviewed'], actions: ['submit', 'paste', 'upload'] }, options: { ...DEFAULT_OPTIONS, severity: 'low', acknowledgeMinutes: 30 } },
  },
  'personal-cloud': {
    label: 'Block confidential uploads to personal cloud storage',
    form: { name: 'Block confidential uploads to personal cloud storage', action: 'block', conditions: { appCategories: { op: 'in', values: ['cloud_storage'] }, accountTypes: ['personal'], actions: ['upload'], data: { minSensitivity: 'confidential' } }, options: { ...DEFAULT_OPTIONS, alert: true } },
  },
  'file-transfer': {
    label: 'Justify uploads to file-transfer sites',
    form: { name: 'Justify uploads to file-transfer sites', action: 'justify', conditions: { appCategories: { op: 'in', values: ['file_transfer'] }, actions: ['upload'] }, options: { ...DEFAULT_OPTIONS, severity: 'low', justificationPrompt: 'Who is receiving this, and why?' } },
  },
  'block-unsanctioned': {
    label: 'Block unsanctioned applications',
    form: { name: 'Block unsanctioned applications', action: 'block', conditions: { appApproval: ['unsanctioned'], actions: ['navigate'] }, options: { ...DEFAULT_OPTIONS, severity: 'low' } },
  },
};

/**
 * Policy areas: a filter over the one policy model (formerly the Protect → AI / Data / Identity pages).
 * Each area names the policies that govern it and the templates that start one.
 */
export type PolicyArea = 'all' | 'web' | 'ai' | 'data' | 'identity';
export const AREAS: Record<Exclude<PolicyArea, 'all'>, { label: string; about: string; match: (c: PolicyRule['conditions']) => boolean; templates: string[] }> = {
  web: {
    label: 'Web',
    about: 'Which sites and applications can be opened at all.',
    match: (c) => !!c.actions?.includes('navigate') || (!c.data && !c.actions),
    templates: ['block-unsanctioned'],
  },
  ai: {
    label: 'AI',
    about: 'Generative AI: corporate accounts, personal accounts and tools nobody has reviewed yet.',
    match: (c) => !!c.appCategories?.values.includes('genai'),
    templates: ['personal-ai', 'unknown-ai'],
  },
  data: {
    label: 'Data',
    about: 'Content inspected at pastes, uploads and submissions — data loss prevention.',
    match: (c) => !!c.data,
    templates: ['personal-cloud', 'file-transfer', 'personal-ai'],
  },
  identity: {
    label: 'Identity',
    about: 'Rules that depend on whether people use a corporate or a personal account.',
    match: (c) => !!c.accountTypes,
    templates: ['personal-ai', 'personal-cloud'],
  },
};
const AREA_OPTIONS: Array<[PolicyArea, string]> = [['all', 'All'], ['web', 'Web'], ['ai', 'AI'], ['data', 'Data'], ['identity', 'Identity']];
export const policyArea = (v: string | null): PolicyArea => (v && v in AREAS ? (v as PolicyArea) : 'all');
export const inArea = (area: PolicyArea, c: PolicyRule['conditions']) => area === 'all' || AREAS[area].match(c ?? {});

export function Policies() {
  const can = useCan();
  const [sp, setSp] = useSearchParams();
  const area = policyArea(sp.get('area'));
  const setArea = (a: PolicyArea) => { const n = new URLSearchParams(sp); if (a === 'all') n.delete('area'); else n.set('area', a); setSp(n, { replace: true }); };
  const q = useApi<{ policyVersion: number; items: any[] }>('/policies');
  const versions = useApi<any[]>('/policy-versions');
  const [turningOff, setTurningOff] = useState<any>(null);
  const toggle = useAction((v: { id: string; enabled: boolean }) => api(`/policies/${v.id}/enabled`, { method: 'PATCH', body: { enabled: v.enabled } }), () => setTurningOff(null));
  // Turning OFF an enabled Block policy removes protection at once: confirm it. Everything else toggles directly.
  const onToggle = (p: any, enabled: boolean) => (!enabled && p.action === 'block' ? setTurningOff(p) : toggle.mutate({ id: p.id, enabled }));
  const nav = useNavigate();
  return (
    <>
      <PageHead
        title="Policies"
        sub={<>Every enabled policy is evaluated for each action. The <b>strongest</b> matching decision wins (Block → Require justification → Warn → Log → Allow); ties go to the lower priority number. An Allow never overrides a stricter policy — use an exception for that.</>}
        actions={can('policies:write') && <Link className="btn primary" to="/protect/policies/new">New policy</Link>}
      />
      <div className="filters">
        <Seg label="Area" value={area} onChange={setArea} options={AREA_OPTIONS} />
        {area !== 'all' && <span className="sub">{AREAS[area].about}</span>}
      </div>
      <MutationError error={turningOff ? null : toggle.error} />
      <Q q={q}>
        {(d) => {
          const rows = d.items.filter((p) => inArea(area, p.conditions));
          return rows.length === 0 ? (
            <Empty title={area === 'all' ? 'No policies yet' : `No ${AREAS[area].label.toLowerCase()} policies yet`}>
              {can('policies:write') ? 'Start from one of the templates below, or create a policy from scratch.' : 'An administrator with policy rights can add one.'}
            </Empty>
          ) : (
            <div className="table-wrap"><table className="t">
              <thead><tr><th className="num">Priority</th><th>Policy</th><th>Decision</th><th>Alert</th><th className="num">Matches (7d)</th><th>Updated</th><th>Enabled</th></tr></thead>
              <tbody>{rows.map((p) => (
                <tr key={p.id} className={p.enabled ? 'clickable' : 'clickable dim'} onClick={() => nav(`/protect/policies/${p.id}`)}>
                  <td className="num">{p.priority}</td>
                  <td><EntityLink to={`/protect/policies/${p.id}`}>{p.name}</EntityLink><div className="explain">{p.summary}</div></td>
                  <td><DecisionTag d={p.action} /></td>
                  <td className="sub">{p.options.alert ? p.options.severity : '—'}</td>
                  <td className="num">{p.hits_7d}</td>
                  <td className="sub"><Ago at={p.updated_at} />{p.updated_by_email && <div>{p.updated_by_email}</div>}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <label className="check"><input type="checkbox" checked={p.enabled} disabled={!can('policies:write') || toggle.isPending} onChange={(e) => onToggle(p, e.target.checked)} aria-label={`Enable ${p.name}`} />{p.enabled ? 'On' : 'Off'}</label>
                  </td>
                </tr>
              ))}</tbody>
            </table></div>
          );
        }}
      </Q>
      {turningOff && (
        <ConfirmDialog title={`Turn off “${turningOff.name}”?`} confirmLabel="Turn off and publish" danger busy={toggle.isPending} error={toggle.error}
          onConfirm={() => toggle.mutate({ id: turningOff.id, enabled: false })} onClose={() => setTurningOff(null)}>
          <p className="explain">{turningOff.summary}</p>
          <p>Browsers stop enforcing it at their next policy check{turningOff.hits_7d > 0 ? ` — it matched ${turningOff.hits_7d} time${turningOff.hits_7d === 1 ? '' : 's'} in the last 7 days` : ''}. A weaker policy may then apply, or the action is allowed. You can turn it back on at any time.</p>
        </ConfirmDialog>
      )}
      {can('policies:write') && <StartFromTemplate area={area} />}
      <div className="cols cols-2">
        <Tester />
        <Section title="Published versions" aside={q.data ? `current v${q.data.policyVersion}` : undefined}>
          <Q q={versions}>
            {(rows) => (
              <table className="t"><thead><tr><th>Version</th><th>Change</th><th className="num">Devices on it</th><th>When</th></tr></thead>
                <tbody>{rows.slice(0, 12).map((v) => <tr key={v.version}><td>v{v.version}</td><td>{v.summary}<div className="sub">{v.created_by_email ?? 'system'}</div></td><td className="num">{v.devices}</td><td className="sub"><Ago at={v.created_at} /></td></tr>)}</tbody></table>
            )}
          </Q>
        </Section>
      </div>
    </>
  );
}

/** Templates for the selected area (all templates under "All"). */
export function templatesFor(area: PolicyArea): string[] {
  return area === 'all' ? Object.keys(TEMPLATES) : AREAS[area].templates;
}

function StartFromTemplate({ area }: { area: PolicyArea }) {
  return (
    <Section title="Start from a template" aside={area === 'all' ? 'every template' : `${AREAS[area].label} templates`}>
      <ul className="m-0 mt-12 indent">
        {templatesFor(area).map((t) => <li key={t}><Link to={`/protect/policies/new?template=${t}`}>{TEMPLATES[t]!.label}</Link></li>)}
      </ul>
      {area === 'identity' && (
        <p className="explain">Accounts count as corporate when their email domain is one of your corporate domains — see <Link to="/admin/settings#accounts">Settings → General</Link>.</p>
      )}
    </Section>
  );
}

/** Evaluates a hypothetical action against the SAVED policy set, using the same engine the extension runs. */
function Tester() {
  const users = useApi<any[]>('/users/options');
  const apps = useApi<any[]>('/apps/options');
  const cls = useApi<any>('/classifications');
  const [f, setF] = useState({ userId: '', appId: '', accountType: 'personal', action: 'paste', classificationIds: [] as string[], deviceManaged: true });
  const run = useAction((b: object) => api('/policies/simulate', { method: 'POST', body: b }));
  return (
    <Section title="Policy tester" aside="what would happen if…">
      <form className="stack-12 mt-12" onSubmit={(e) => {
        e.preventDefault();
        const app = apps.data?.find((a) => a.id === f.appId);
        run.mutate({ userId: f.userId || null, appId: f.appId || null, accountType: f.accountType, action: f.action, classificationIds: f.classificationIds, deviceManaged: f.deviceManaged, ...(app ? {} : { category: 'unknown' }) });
      }}>
        <div className="form-grid">
          <label className="field">User<select value={f.userId} onChange={(e) => setF({ ...f, userId: e.target.value })}><option value="">Unidentified</option>{users.data?.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select></label>
          <label className="field">Application<select value={f.appId} onChange={(e) => setF({ ...f, appId: e.target.value })}><option value="">Unknown site</option>{apps.data?.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></label>
          <label className="field">Account<select value={f.accountType} onChange={(e) => setF({ ...f, accountType: e.target.value })}>{ACCOUNT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></label>
          <label className="field">Action<select value={f.action} onChange={(e) => setF({ ...f, action: e.target.value })}>{ACTIONS.map((a) => <option key={a} value={a}>{actionLabel(a)}</option>)}</select></label>
          <label className="field">Data contains
            <select multiple size={4} value={f.classificationIds} onChange={(e) => setF({ ...f, classificationIds: Array.from(e.target.selectedOptions, (o) => o.value) })}>
              {cls.data?.classifications.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label className="check self-end"><input type="checkbox" checked={f.deviceManaged} onChange={(e) => setF({ ...f, deviceManaged: e.target.checked })} /> Managed device</label>
        </div>
        <div><button className="btn">Evaluate</button></div>
      </form>
      <MutationError error={run.error} />
      {run.data && (
        <Notice className="mt-12" aria-live="polite">
          <DecisionTag d={run.data.decision} /> {run.data.ruleName ? <>by <b>{run.data.ruleName}</b></> : 'no policy matched (default allow)'}
          {run.data.matchedRuleIds.length > 1 && <div className="sub">{run.data.matchedRuleIds.length} policies matched; the strongest decision applies.</div>}
          {run.data.exemptedRuleIds.length > 0 && <div className="sub">{run.data.exemptedRuleIds.length} policy exempted by an exception.</div>}
        </Notice>
      )}
    </Section>
  );
}

/**
 * Link to Protect → Exceptions with the new-exception form prefilled. Parameter names are shared
 * with the Exceptions page and the alert detail link: `policy`, `user`, `app` (ids).
 */
export function exceptionLink(p: { policy?: string | null; user?: string | null; app?: string | null }): string {
  const qs = new URLSearchParams();
  for (const k of ['policy', 'user', 'app'] as const) if (p[k]) qs.set(k, p[k]!);
  const s = qs.toString();
  return `/protect/exceptions${s ? `?${s}` : ''}`;
}

// ─────────────────────────── Editor ───────────────────────────
function initialForm(template: string | null): PolicyForm {
  const t = template ? TEMPLATES[template]?.form : undefined;
  return { name: '', description: '', enabled: true, priority: 100, conditions: {}, action: 'block', options: { ...DEFAULT_OPTIONS }, ...(t ?? {}) } as PolicyForm;
}

export function PolicyEditor() {
  const { id } = useParams();
  const loc = useLocation();
  const template = new URLSearchParams(loc.search).get('template');
  const existing = useApi<any>(id ? `/policies/${id}` : null);
  if (id && existing.isLoading) return <Q q={existing}>{() => null}</Q>;
  if (id && existing.error) return <Q q={existing}>{() => null}</Q>;
  const base: PolicyForm = id && existing.data
    ? { name: existing.data.name, description: existing.data.description, enabled: existing.data.enabled, priority: existing.data.priority, conditions: existing.data.conditions, action: existing.data.action, options: { ...DEFAULT_OPTIONS, ...existing.data.options } }
    : initialForm(template);
  return <Editor key={id ?? `new-${template}`} id={id ?? null} base={base} detail={existing.data} />;
}

function Editor({ id, base, detail }: { id: string | null; base: PolicyForm; detail: any }) {
  const can = useCan();
  const nav = useNavigate();
  const [f, setF] = useState<PolicyForm>(base);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const users = useApi<any[]>('/users/options');
  const groups = useApi<any[]>('/groups');
  const apps = useApi<any[]>('/apps/options');
  const cls = useApi<any>('/classifications');
  const save = useAction(() => api(id ? `/policies/${id}` : '/policies', { method: id ? 'PUT' : 'POST', body: f }), (r: any) => nav(`/protect/policies/${r.id}`, { replace: true }));
  const del = useAction(() => api(`/policies/${id}`, { method: 'DELETE' }), () => nav('/protect/policies'));
  const ro = !can('policies:write');

  const names = useMemo(() => ({
    user: new Map((users.data ?? []).map((u) => [u.id, u.name] as [string, string])),
    group: new Map((groups.data ?? []).map((g) => [g.id, g.name] as [string, string])),
    app: new Map((apps.data ?? []).map((a) => [a.id, a.name] as [string, string])),
    cls: new Map<string, string>((cls.data?.classifications ?? []).map((c: any) => [c.id, c.name] as [string, string])),
    cat: new Map(APP_CATEGORIES.map((c) => [c, catLabel(c)] as [string, string])),
  }), [users.data, groups.data, apps.data, cls.data]);

  const c = f.conditions;
  const setC = (patch: Partial<RuleConditions>) => {
    const next = { ...c, ...patch } as Record<string, unknown>;
    for (const k of Object.keys(next)) if (next[k] === undefined) delete next[k];
    setF({ ...f, conditions: next as RuleConditions });
  };
  const setO = (patch: Partial<PolicyRule['options']>) => setF({ ...f, options: { ...f.options, ...patch } });
  const setIn = (key: 'users' | 'groups' | 'apps' | 'appCategories', values: string[], op: 'in' | 'not_in' = (c[key] as any)?.op ?? 'in') =>
    setC({ [key]: values.length ? { op, values } : undefined } as Partial<RuleConditions>);

  const summary = explainRule({ conditions: f.conditions, action: f.action }, {
    user: (x) => names.user.get(x), group: (x) => names.group.get(x), app: (x) => names.app.get(x), classification: (x) => names.cls.get(x),
  });
  const warnings: string[] = [];
  const noScope = !c.appCategories && !c.apps && !c.appApproval && !c.data && !c.actions;
  if (f.action === 'block' && noScope) warnings.push('This blocks every action on every site for everyone in scope. Narrow it by destination, action or data.');
  if (f.action === 'block' && c.actions?.includes('navigate') && !c.apps && !c.appCategories && !c.appApproval) warnings.push('Blocking navigation without a destination condition blocks all browsing.');
  if (c.data && c.actions?.every((a) => a === 'navigate' || a === 'download')) warnings.push('Data conditions only apply to pastes, uploads and submissions — navigation and downloads are not inspected.');
  if (f.action === 'allow') warnings.push('Allow never overrides a stricter policy. To exempt people from a policy, create an exception.');

  const who = [...(c.groups?.values ?? []).map((g) => names.group.get(g) ?? '?'), ...(c.users?.values ?? []).map((u) => names.user.get(u) ?? '?')];
  const whoLabel = who.length === 0 ? 'anyone' : `${(c.groups?.op ?? c.users?.op) === 'not_in' ? 'anyone except ' : ''}${who.length <= 2 ? who.join(' or ') : `${who.slice(0, 2).join(', ')} +${who.length - 2}`}`;
  const destLabel = c.apps ? `${c.apps.op === 'not_in' ? 'any app except ' : ''}${summarize(c.apps.values, names.app, 'any application')}` : c.appCategories ? `${c.appCategories.op === 'not_in' ? 'anything except ' : ''}${summarize(c.appCategories.values, names.cat, 'any application')}` : 'any application';
  const dataLabel = c.data ? [c.data.classifications?.length ? summarize(c.data.classifications, names.cls, '') : null, c.data.minSensitivity ? `${c.data.minSensitivity}+ data` : null, c.data.orUninspectable ? 'uninspectable content' : null].filter(Boolean).join(' or ') : 'any data';

  return (
    <>
      <PageHead
        crumbs={<Link to="/protect/policies">Policies</Link>}
        title={id ? f.name || 'Untitled policy' : 'New policy'}
        sub={id && detail ? <>Revision {detail.revision} · updated {fmtDate(detail.updated_at)}</> : 'Build the rule as a sentence. Click any boxed part to change it.'}
        actions={id ? (
          can('exceptions:write') && <Link className="btn" to={exceptionLink({ policy: id })} title="Exempt specific people, groups or applications from this policy">Add exception</Link>
        ) : (
          <select aria-label="Start from template" value="" onChange={(e) => e.target.value && setF(initialForm(e.target.value))}>
            <option value="">Start from a template…</option>
            {Object.entries(TEMPLATES).map(([k, t]) => <option key={k} value={k}>{t.label}</option>)}
          </select>
        )}
      />
      <fieldset className="m-0 p-0 border-0" disabled={ro}>
        <label className="field max-w-field">Policy name<input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} maxLength={120} placeholder="e.g. Block customer data to personal AI" required /></label>

        <div className="sentence mt-16">
          <span className="clause">
            <span className="kw">WHEN</span>
            <Token ariaLabel="Who" set={who.length > 0} label={whoLabel}>
              {() => (
                <div>
                  <div className="seg mb-8">
                    {(['in', 'not_in'] as const).map((op) => (
                      <button type="button" key={op} aria-pressed={(c.groups?.op ?? c.users?.op ?? 'in') === op} onClick={() => setC({ groups: c.groups ? { ...c.groups, op } : undefined, users: c.users ? { ...c.users, op } : undefined })}>{op === 'in' ? 'Only these' : 'Everyone except'}</button>
                    ))}
                  </div>
                  <h3>Groups</h3>
                  <MultiPick searchable options={(groups.data ?? []).map((g) => [g.id, g.name, g.source === 'entra' ? 'Entra ID' : 'Local'] as [string, string, string])} value={c.groups?.values ?? []} onChange={(v) => setIn('groups', v, c.groups?.op ?? c.users?.op)} />
                  <h3 className="mt-8">People</h3>
                  <MultiPick searchable options={(users.data ?? []).map((u) => [u.id, u.name, u.email] as [string, string, string])} value={c.users?.values ?? []} onChange={(v) => setIn('users', v, c.users?.op ?? c.groups?.op)} />
                </div>
              )}
            </Token>{' '}
            using{' '}
            <Token ariaLabel="Device" set={!!c.deviceManaged} label={c.deviceManaged ? `${c.deviceManaged} devices` : 'any device'}>
              {(close) => <SinglePick close={close} value={(c.deviceManaged ?? '') as '' | 'managed' | 'unmanaged'} onChange={(v) => setC({ deviceManaged: v || undefined })} options={[['', 'Any device'], ['managed', 'Managed devices', 'Configured by MDM (Intune)'], ['unmanaged', 'Unmanaged devices']]} />}
            </Token>{' '}
            in{' '}
            <Token ariaLabel="Browser" set={!!c.browsers} label={c.browsers ? c.browsers.map((b) => BROWSER_SHORT[b] ?? 'other').join(' or ') : 'any browser'}>
              {() => <MultiPick options={[['edge', 'Microsoft Edge'], ['chrome', 'Google Chrome'], ['firefox', 'Firefox'], ['safari', 'Safari'], ['other', 'Other']]} value={c.browsers ?? []} onChange={(v) => setC({ browsers: v.length ? (v as any) : undefined })} />}
            </Token>
          </span>
          <span className="clause">
            with a{' '}
            <Token ariaLabel="Account type" set={!!c.accountTypes} label={c.accountTypes ? c.accountTypes.map((t) => (t === 'unknown' ? 'unrecognised' : t)).join(' or ') : 'any'}>
              {() => <MultiPick options={[['corporate', 'Corporate', 'Signed in with your organisation’s identity'], ['personal', 'Personal', 'Consumer accounts (gmail.com, outlook.com…)'], ['unknown', 'Unrecognised', 'Account not detected, or another organisation’s']]} value={c.accountTypes ?? []} onChange={(v) => setC({ accountTypes: v.length ? (v as any) : undefined })} />}
            </Token>{' '}
            account, accesses{' '}
            <Token ariaLabel="Destination" set={!!(c.apps || c.appCategories)} label={destLabel}>
              {() => (
                <div>
                  <h3>Categories</h3>
                  <MultiPick options={APP_CATEGORIES.map((x) => [x, catLabel(x)] as [string, string])} value={c.appCategories?.values ?? []} onChange={(v) => setIn('appCategories', v)} />
                  <h3 className="mt-8">Specific applications</h3>
                  <MultiPick searchable options={(apps.data ?? []).map((a) => [a.id, a.name, a.hosts[0]] as [string, string, string])} value={c.apps?.values ?? []} onChange={(v) => setIn('apps', v)} />
                </div>
              )}
            </Token>{' '}
            that {c.appApproval && c.appApproval.length === 1 ? 'is' : 'are'}{' '}
            <Token ariaLabel="Approval" set={!!c.appApproval} label={c.appApproval ? c.appApproval.join(' or ') : 'any approval status'}>
              {() => <MultiPick options={APP_APPROVALS.map((a) => [a, a[0]!.toUpperCase() + a.slice(1), a === 'unreviewed' ? 'Includes newly discovered and suspected AI tools' : undefined] as [string, string, string?])} value={c.appApproval ?? []} onChange={(v) => setC({ appApproval: v.length ? (v as any) : undefined })} />}
            </Token>
          </span>
          <span className="clause">
            <span className="kw">AND</span> attempts{' '}
            <Token ariaLabel="Actions" set={!!c.actions} label={c.actions ? c.actions.map(actionLabel).join(' or ') : 'any action'}>
              {() => <MultiPick options={ACTIONS.map((a) => [a, actionLabel(a)[0]!.toUpperCase() + actionLabel(a).slice(1), a === 'submit' ? 'AI prompts and forms' : a === 'navigate' ? 'Opening the site' : undefined] as [string, string, string?])} value={c.actions ?? []} onChange={(v) => setC({ actions: v.length ? (v as any) : undefined })} />}
            </Token>
          </span>
          <span className="clause">
            <span className="kw">AND</span> data contains{' '}
            <Token ariaLabel="Data" set={!!c.data} label={dataLabel}>
              {() => (
                <div>
                  <h3>At least this sensitive</h3>
                  <select value={c.data?.minSensitivity ?? ''} onChange={(e) => {
                    const minSensitivity = (e.target.value || undefined) as any;
                    const classifications = c.data?.classifications;
                    setC({ data: minSensitivity || classifications?.length ? { ...(classifications?.length ? { classifications } : {}), ...(minSensitivity ? { minSensitivity } : {}) } : undefined });
                  }}>
                    <option value="">—</option>{SENSITIVITY_LEVELS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <h3 className="mt-8">Or any of these classifications</h3>
                  <MultiPick options={(cls.data?.classifications ?? []).map((x: any) => [x.id, x.name, x.sensitivity] as [string, string, string])} value={c.data?.classifications ?? []} onChange={(v) => {
                    const minSensitivity = c.data?.minSensitivity;
                    setC({ data: v.length || minSensitivity ? { ...(v.length ? { classifications: v } : {}), ...(minSensitivity ? { minSensitivity } : {}) } : undefined });
                  }} />
                  <label className="opt mt-8">
                    <input type="checkbox" checked={!!c.data?.orUninspectable} onChange={(e) => {
                      const d = { ...(c.data ?? {}), orUninspectable: e.target.checked || undefined } as any;
                      if (!d.orUninspectable) delete d.orUninspectable;
                      setC({ data: d.classifications?.length || d.minSensitivity || d.orUninspectable ? d : undefined });
                    }} />
                    <span>Or the content could not be fully inspected<span className="opt-sub">PDFs, images, archives, oversized content — treat as risky</span></span>
                  </label>
                  <p className="explain">Content is inspected on the device only. Nothing is uploaded.</p>
                </div>
              )}
            </Token>
            {(c.file || !c.actions || c.actions.some((a) => a === 'upload' || a === 'download')) && (
              <>
                {' '}in{' '}
                <Token ariaLabel="File types" set={!!c.file?.extensions?.length} label={c.file?.extensions?.length ? `.${c.file.extensions.join(' or .')} files${c.file.minSizeBytes ? ` ≥ ${c.file.minSizeBytes} bytes` : ''}` : `any file${c.file?.minSizeBytes ? ` ≥ ${c.file.minSizeBytes} bytes` : ''}`}>
                  {() => <FileTypes value={c.file?.extensions ?? []} onChange={(exts) => setC({ file: exts.length || c.file?.minSizeBytes ? { ...(exts.length ? { extensions: exts } : {}), ...(c.file?.minSizeBytes ? { minSizeBytes: c.file.minSizeBytes } : {}) } : undefined })} />}
                </Token>
              </>
            )}
          </span>
          <span className="clause mt-8">
            <span className="kw">THEN</span>
            <Token ariaLabel="Decision" set tone={statusTone(DECISION, f.action)} label={DECISION_LABELS[f.action]}>
              {(close) => <SinglePick close={close} value={f.action} onChange={(v) => setF({ ...f, action: v })} options={[
                ['block', 'Block', 'Stop the action and explain why'],
                ['justify', 'Require justification', 'Allow after the user gives a business reason'],
                ['warn', 'Warn', 'Allow after the user acknowledges the risk'],
                ['log', 'Allow and log', 'Record the action silently'],
                ['allow', 'Allow', 'Explicitly permitted (never overrides stricter policies)'],
              ]} />}
            </Token>
          </span>
        </div>

        <p className="explain mt-12" aria-live="polite"><b>In plain English:</b> {summary}</p>
        {warnings.map((w) => <Notice tone="warn" className="mt-8" key={w}>{w}</Notice>)}

        <Section title="What the user sees and what you get">
          <div className="form-grid mt-12">
            {f.action !== 'allow' && f.action !== 'log' && (
              <label className="field span-all">Explanation for the user <span className="hint">Shown with the detected risk. Suggest the safe alternative.</span>
                <input value={f.options.userMessage ?? ''} onChange={(e) => setO({ userMessage: e.target.value || undefined })} maxLength={500} placeholder="Use your approved corporate AI account instead." />
              </label>
            )}
            {f.action === 'justify' && (
              <label className="field">Justification question<input value={f.options.justificationPrompt ?? ''} onChange={(e) => setO({ justificationPrompt: e.target.value || undefined })} maxLength={300} placeholder="Why is this needed?" /></label>
            )}
            {f.action === 'warn' && (
              <label className="field">Remember acknowledgement for <span className="hint">minutes, per site</span><input type="number" min={0} max={1440} value={f.options.acknowledgeMinutes} onChange={(e) => setO({ acknowledgeMinutes: Number(e.target.value) })} /></label>
            )}
            <label className="check"><input type="checkbox" checked={f.options.alert} onChange={(e) => setO({ alert: e.target.checked })} /> Generate an alert</label>
            {f.options.alert && <label className="field">Alert severity<select value={f.options.severity} onChange={(e) => setO({ severity: e.target.value as any })}>{SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}</select></label>}
            <label className="check"><input type="checkbox" checked={f.options.notifyIntegrations} onChange={(e) => setO({ notifyIntegrations: e.target.checked })} /> Send matching events to SIEM / webhooks</label>
          </div>
        </Section>

        <details className="mt-16">
          <summary><b>Advanced</b> <span className="sub">priority {f.priority}{f.description ? ' · description' : ''}</span></summary>
          <div className="form-grid mt-12">
            <label className="field">Priority <span className="hint">lower wins ties between equal decisions</span><input type="number" min={0} max={100000} value={f.priority} onChange={(e) => setF({ ...f, priority: Number(e.target.value) })} /></label>
            <label className="field span-all">Description<textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} maxLength={1000} /></label>
          </div>
        </details>
        <label className="check mt-16"><input type="checkbox" checked={f.enabled} onChange={(e) => setF({ ...f, enabled: e.target.checked })} /> Enabled</label>
      </fieldset>
      <MutationError error={save.error ?? del.error} />
      {!ro && (
        <div className="actions mt-16">
          <button className="btn primary" disabled={!f.name.trim() || save.isPending} onClick={() => save.mutate(undefined)}>{save.isPending ? 'Publishing…' : id ? 'Save and publish' : 'Create and publish'}</button>
          <Link className="btn" to="/protect/policies">Cancel</Link>
          {id && (confirmDelete
            ? <><span className="muted">Delete this policy? Devices stop enforcing it at their next check.</span><button className="btn danger" onClick={() => del.mutate(undefined)}>Delete</button><button className="btn" onClick={() => setConfirmDelete(false)}>Keep</button></>
            : <button className="btn danger" onClick={() => setConfirmDelete(true)}>Delete…</button>)}
        </div>
      )}
      {ro && <p className="muted">Your role can view policies but not change them.</p>}
      {detail?.revisions?.length > 0 && (
        <Section title="Revision history">
          <table className="t"><thead><tr><th>Revision</th><th>Change</th><th>By</th><th>When</th><th>Decision</th></tr></thead>
            <tbody>{detail.revisions.map((r: any) => <tr key={r.revision}><td>{r.revision}</td><td>{r.change_type}</td><td className="sub">{r.changed_by_email ?? 'system'}</td><td className="sub">{fmtDate(r.created_at)}</td><td><DecisionTag d={r.snapshot.action} /></td></tr>)}</tbody></table>
        </Section>
      )}
      {id && <RecentMatches id={id} />}
    </>
  );
}

/** File-type picker inside the rule sentence. Keeps the raw text while typing; stores lower-case extensions without dots. */
function FileTypes({ value, onChange }: { value: string[]; onChange: (exts: string[]) => void }) {
  const [text, setText] = useState(value.join(' '));
  return (
    <div className="min-w-field">
      <label className="field">File types <span className="hint">e.g. xlsx csv — empty means any file</span>
        <input autoFocus value={text} onChange={(e) => { setText(e.target.value); onChange([...new Set(e.target.value.toLowerCase().split(/[\s,.]+/).filter(Boolean))]); }} placeholder="xlsx csv" />
      </label>
      <p className="explain">Applies to uploads and downloads; pastes and form submissions have no file.</p>
    </div>
  );
}

function RecentMatches({ id }: { id: string }) {
  const q = useApi<{ items: any[] }>(`/events?ruleId=${id}&limit=10`);
  return (
    <Section title="Recent matches">
      <Q q={q}>{(d) => d.items.length === 0 ? <Empty title="No matches yet" /> : (
        <table className="t"><tbody>{d.items.map((e) => <tr key={e.id}><td className="sub"><Ago at={e.occurred_at} /></td><td><Outcome o={e.outcome} /></td><td>{e.user?.name ?? e.reported_email ?? '—'}</td><td>{e.app?.name ?? e.hostname}</td></tr>)}</tbody></table>
      )}</Q>
    </Section>
  );
}
